<template>
  <v-container>
    <div class="d-flex flex-column" :style="{ gap: '16px' }">
      <div class="d-flex" :style="{ justifyContent: 'space-between', alignItems: 'center' }">
        <h1 class="text-h5">Downtime Reports</h1>
        <v-btn color="primary" dark @click="$router.push({ name: 'DowntimeReports.Create' })">
          <v-icon left>mdi-plus</v-icon> New Report
        </v-btn>
      </div>

      <div class="d-flex flex-wrap" :style="{ gap: '10px', alignItems: 'center' }">
        <!-- Search -->
        <v-text-field
          v-model="search"
          outlined
          dense
          label="Search"
          hide-details="auto"
          prepend-inner-icon="mdi-magnify"
          clearable
          @input="fetchReportsDebounced"
          style="max-width: 200px"
        ></v-text-field>

        <!-- Customer -->
        <v-autocomplete
          outlined
          dense
          hide-details="auto"
          label="Customer"
          name="customer_id"
          item-value="id"
          :item-text="item => `${item.id} - ${item.name}`"
          :items="customers"
          chips
          v-model="customerIds"
          multiple
        />
        <!-- Status Filter -->
        <v-select
          v-model="status"
          :items="statusOptions"
          outlined
          dense
          hide-details="auto"
          label="Status"
          multiple
          clearable
          style="max-width: 180px"
          @change="fetchReportsDebounced"
        ></v-select>

        <!-- Errors Filter -->
        <v-autocomplete
          v-model="errors"
          :items="errorOptions"
          multiple
          outlined
          dense
          hide-details="auto"
          chips
          clearable
          label="Errors"
          style="max-width: 220px"
          @change="fetchReportsDebounced"
        />

        <!-- Date Start Picker -->
        <v-menu
          ref="startMenu"
          v-model="startMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="dateStart"
              label="Start Date"
              readonly
              hide-details="auto"
              v-bind="attrs"
              v-on="on"
              dense
              outlined
              style="max-width: 150px"
              clearable
            ></v-text-field>
          </template>
          <v-date-picker v-model="dateStart" @input="startMenu = false"></v-date-picker>
        </v-menu>

        <!-- Date End Picker -->
        <v-menu
          ref="endMenu"
          v-model="endMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="dateEnd"
              label="End Date"
              readonly
              hide-details="auto"
              v-bind="attrs"
              v-on="on"
              dense
              outlined
              style="max-width: 150px"
              clearable
            ></v-text-field>
          </template>
          <v-date-picker v-model="dateEnd" @input="endMenu = false"></v-date-picker>
        </v-menu>

        <v-spacer></v-spacer>
        <v-btn outlined small color="primary" @click="fetchReports">
          <v-icon left>mdi-refresh</v-icon> Refresh
        </v-btn>
        <v-btn outlined small color="green" @click="printSummary">
          <v-icon left>mdi-printer</v-icon>
          Print Summary
        </v-btn>
        <v-btn outlined small color="teal" @click="downloadCsvSummary">
          <v-icon left>mdi-file-delimited</v-icon>
          Download CSV
        </v-btn>
      </div>

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="reports"
        :loading="loading"
        :page.sync="page"
        :items-per-page.sync="limit"
        :server-items-length="total"
        dense
        outlined
        disable-sort
        class="elevation-1"
      >
        <template #item.form_number="{ item }">
          <v-btn text small color="primary" @click="goToEdit(item)">
            {{ item.form_number || 'N/A' }}
          </v-btn>
        </template>

        <template #item.customer_id="{ item }">
          <span>{{ item.company || `Customer ${item.customer_id}` }}</span>
        </template>

        <template #item.machine_type="{ item }">
          <span>{{ item.machine_type || '—' }}</span>
        </template>

        <template #item.reported_at="{ value }">
          <span>{{ formatDate(value) }}</span>
        </template>

        <template #item.date_start="{ value }">
          <span>{{ formatDate(value) }}</span>
        </template>

        <template #item.date_end="{ value }">
          <span>{{ formatDate(value) }}</span>
        </template>

        <template #item.purchase_needed="{ item }">
          <v-chip small :color="item.purchase_needed ? 'red lighten-2' : 'green lighten-2'" dark>
            {{ item.purchase_needed ? 'Yes' : 'No' }}
          </v-chip>
        </template>
        <template #item.status="{ item }">
          <v-chip small :color="statusColor(item.status)" dark class="text-capitalize">
            {{ item.status || '—' }}
          </v-chip>
        </template>
        <template #item.actions="{ item }">
          <div class="d-flex" :style="{ gap: '4px' }">
            <v-btn
              icon
              small
              color="purple"
              @click="
                $router.push({
                  name: 'DowntimeReports.Show',
                  params: { downtimeReportId: item.id },
                })
              "
              ><v-icon small>mdi-eye</v-icon></v-btn
            >
            <v-btn v-if="!item.locked_at" icon small color="primary" @click="goToEdit(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn v-if="!item.locked_at" icon small color="red" @click="confirmDelete(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
            <v-btn
              v-if="item.locked_at && canUnlock"
              icon
              small
              color="orange darken-2"
              @click="confirmUnlock(item)"
            >
              <v-icon small>mdi-lock-open</v-icon>
            </v-btn>
            <v-btn icon small color="green" @click="printReport(item.id)">
              <v-icon small>mdi-file</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Pagination -->
      <div class="d-flex justify-end align-center">
        <v-pagination v-model="page" :length="pages" @input="fetchReports"></v-pagination>
      </div>
    </div>
  </v-container>
</template>

<script>
import appAxios from '@/libs/app-axios'
import { ErrorPopup, LoadingPopup, SuccessPopup } from '@/libs/utils'
import _ from 'lodash'

export default {
  name: 'DowntimeReportIndex',
  data() {
    return {
      customers: [],
      customerIds: [],
      search: '',
      reports: [],
      total: 0,
      page: 1,
      limit: 10,
      loading: false,
      dateStart: null,
      dateEnd: null,
      errors: [],
      errorOptions: [],
      status: [],
      statusOptions: ['pending', 'ongoing', 'cancelled', 'completed'], // customize as needed
      startMenu: false,
      endMenu: false,
      headers: [
        { text: 'Form #', value: 'form_number', align: 'start' },
        { text: 'Customer', value: 'customer_id' },
        { text: 'Subject', value: 'subject' },
        // { text: 'Machine Type', value: 'machine_type' },
        { text: 'Unit', value: 'unit_id' },
        { text: 'Date Start', value: 'date_start' },
        { text: 'Date End', value: 'date_end' },
        { text: 'Reported At', value: 'reported_at' },
        { text: 'Purchase Needed', value: 'purchase_needed', align: 'center' },
        { text: 'Status', value: 'status', align: 'center' }, // added status
        { text: 'Actions', value: 'actions', align: 'center', sortable: false },
      ],
    }
  },
  computed: {
    pages() {
      return Math.ceil(this.total / this.limit) || 1
    },
    canUnlock() {
      return this.$store.state.auth.accessLevel >= 7
    },
  },
  created() {
    this.fetchReports()
    this.fetchCustomers()
    this.fetchErrorOptions()
  },
  watch: {
    dateStart() {
      this.fetchReportsDebounced()
    },
    dateEnd() {
      this.fetchReportsDebounced()
    },
    search() {
      this.fetchReportsDebounced()
    },
    customerIds() {
      this.fetchReportsDebounced()
    },
  },
  methods: {
    fetchReportsDebounced: _.debounce(function() {
      this.page = 1
      // @ts-ignore
      this?.fetchReports()
    }, 300),
    statusColor(status) {
      switch (status) {
        case 'pending':
          return 'orange lighten-2'
        case 'ongoing':
          return 'blue lighten-2'
        case 'completed':
          return 'green lighten-2'
        case 'cancelled':
          return 'red lighten-2'
        default:
          return 'grey lighten-2'
      }
    },
    async fetchReports() {
      try {
        this.loading = true
        const axios = appAxios()
        const { data } = await axios.get('/v3/downtime-reports', {
          params: {
            page: this.page,
            limit: this.limit,
            search: this.search,
            dateStart: this.dateStart,
            customerIds: this.customerIds,
            dateEnd: this.dateEnd,
            status: this.status,
            errors: this.errors, // <-- added
          },
        })
        this.reports = data.data || []
        this.total = data.count || 0
      } catch (error) {
        ErrorPopup.fire('Failed to fetch reports', error)
      } finally {
        this.loading = false
      }
    },
    goToEdit(item) {
      this.$router.push({
        name: 'DowntimeReports.Edit',
        params: { downtimeReportId: item.id },
      })
    },
    async confirmDelete(item) {
      const confirmed = await this.$confirm('Are you sure you want to delete this report?')
      if (!confirmed) return
      this.deleteReport(item)
    },
    async deleteReport(item) {
      try {
        LoadingPopup.fire('Deleting', 'Please wait...')
        const axios = appAxios()
        await axios.delete(`/v3/downtime-reports/${item.id}`)
        SuccessPopup.fire('Deleted', 'Downtime report deleted successfully')
        this.fetchReports()
      } catch (error) {
        ErrorPopup.fire('Failed to delete report', error)
      } finally {
        LoadingPopup.close()
      }
    },
    async confirmUnlock(item) {
      const confirmed = await this.$confirm(
        'Unlock this report so it can be edited? It locks again on save while the status is completed.'
      )
      if (!confirmed) return
      this.unlockReport(item)
    },
    async unlockReport(item) {
      try {
        LoadingPopup.fire('Unlocking', 'Please wait...')
        const axios = appAxios()
        await axios.post(`/v3/downtime-reports/${item.id}/unlock`)
        SuccessPopup.fire('Unlocked', 'Downtime report unlocked successfully')
        this.fetchReports()
      } catch (error) {
        ErrorPopup.fire('Failed to unlock report', error)
      } finally {
        LoadingPopup.close()
      }
    },
    /**
     * Description
     * @param {number} downtimeReportId
     */
    async printReport(downtimeReportId) {
      try {
        LoadingPopup.fire('Loading', 'Preparing Report...')
        const axios = appAxios()
        const { data } = await axios.get(`/v3/downtime-reports/${downtimeReportId}/pdf-v2`, {
          responseType: 'blob',
        })

        // Create a blob URL
        const fileURL = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
        window.open(fileURL, '_blank') // open in new tab/window
        SuccessPopup.fire({
          title: 'Success',
          text: 'successfully generated the pdf',
          toast: true,
          showCloseButton: false,
          timer: 3000,
        })
      } catch (e) {
        ErrorPopup.fire('Error', e.message)
      }
    },
    async fetchErrorOptions() {
      try {
        const axios = appAxios()
        const { data: body } = await axios.get('/v3/downtime-reports/error-options')
        this.errorOptions = body.data
      } catch (e) {
        ErrorPopup.fire(
          'Error',
          'Oops something went wrong while getting errorOptions retrying in 3 sec'
        )
        setTimeout(this.fetchErrorOptions, 3000)
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    },
    async printSummary() {
      try {
        LoadingPopup.fire('Loading', 'Preparing Summary PDF...')
        const axios = appAxios()

        const { data } = await axios.get('/v3/downtime-reports/pdf-summary', {
          responseType: 'blob',
          params: {
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            customerIds: this.customerIds,
            status: this.status,
            errors: this.errors,
            search: this.search,
            // optional: add customer filter later if you have it
            // customerId: this.customerId,
          },
        })

        const fileURL = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))

        window.open(fileURL, '_blank')

        SuccessPopup.fire({
          title: 'Success',
          text: 'Downtime summary generated',
          toast: true,
          timer: 3000,
          showCloseButton: false,
        })
      } catch (e) {
        ErrorPopup.fire('Error', 'Failed to generate summary PDF')
      } finally {
        LoadingPopup.close()
      }
    },

    async fetchCustomers() {
      const axios = appAxios()
      const { data } = await axios.get('/v3/customers/dropdown-options')
      this.customers = data.data || []
    },
    async downloadCsvSummary() {
      try {
        LoadingPopup.fire('Loading', 'Preparing CSV export...')
        const axios = appAxios()

        const { data } = await axios.get('/v3/downtime-reports/csv-summary', {
          responseType: 'blob',
          params: {
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            customerIds: this.customerIds, // ARRAY ✅
            status: this.status, // ARRAY ✅
            errors: this.errors, // ARRAY ✅
            search: this.search,
          },
        })

        const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `downtime-reports-${new Date().toISOString().slice(0, 10)}.csv`
        )

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        SuccessPopup.fire({
          title: 'Success',
          text: 'CSV downloaded successfully',
          toast: true,
          timer: 3000,
          showCloseButton: false,
        })
      } catch (e) {
        ErrorPopup.fire('Error', 'Failed to download CSV')
      } finally {
        LoadingPopup.close()
      }
    },
  },
}
</script>
